/**
 * Login Page Component
 * Admin authentication page - Multi-step login flow matching HTML design
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { apiService } from '../services/apiService';
import { tokenManager } from '../utils/tokenManager';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const { login, loadUser, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Step management
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 'forgot' | 'resetSent'>(1);
  const [stepError, setStepError] = useState<{ step: number | string; message: string } | null>(null);
  
  // Step 1: Credentials
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Step 2: PIN
  const [pin, setPin] = useState(['', '', '', '']);
  const pinInputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const [dateSuffix, setDateSuffix] = useState({ day: '', month: '' });
  
  // Step 3: 2FA
  const [twofaMethod, setTwofaMethod] = useState<'app' | 'sms'>('app');
  const [twofaCode, setTwofaCode] = useState(['', '', '', '', '', '']);
  const codeInputRefs = [
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)
  ];
  
  // Forgot password
  const [forgotEmail, setForgotEmail] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Initialize date suffix on mount
  useEffect(() => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    setDateSuffix({
      day: day,
      month: month
    });
  }, []);

  const goToStep = (step: 1 | 2 | 3 | 'forgot' | 'resetSent') => {
    setCurrentStep(step);
    setStepError(null);
  };

  // Step 1: Validate credentials
  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStepError(null);
    
    if (!email || password.length < 4) {
      setStepError({ step: 1, message: 'Please enter valid credentials' });
      return;
    }

    try {
      const response = await apiService.adminLogin(email, password);
      
      // Check if first login is required
      if (response.requiresFirstLogin) {
        // Save token temporarily and redirect to first login setup
        tokenManager.saveTokens(response.token, response.refreshToken, email);
        // Don't call login() - just navigate directly to avoid setting isAuthenticated
        navigate('/first-login');
        return;
      }
      
      // Check if 2FA is required
      if (response.requires2FA) {
        // Save token temporarily and proceed to 2FA step
        tokenManager.saveTokens(response.token, response.refreshToken, email);
        setTwofaMethod(response.twofaMethod || 'app');
        goToStep(3);
        return;
      }
      
      // Normal login - complete login which will handle redirect
      await login({ email, password });
    } catch (err) {
      setStepError({ step: 1, message: err instanceof Error ? err.message : 'Login failed' });
    }
  };

  // PIN input handlers
  const handlePinChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Only numbers
    
    const newPin = [...pin];
    newPin[index] = value.slice(-1); // Only take last character
    setPin(newPin);

    // Auto-focus next input
    if (value && index < 3) {
      pinInputRefs[index + 1].current?.focus();
    }

    // Enable submit button when all filled
    if (newPin.every(d => d !== '')) {
      // PIN is ready
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinInputRefs[index - 1].current?.focus();
    }
  };

  // Validate PIN
  const validatePin = () => {
    if (pin.every(d => d !== '')) {
      goToStep(3);
    } else {
      setStepError({ step: 2, message: 'Please enter your 4-digit PIN' });
    }
  };

  // 2FA code input handlers
  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...twofaCode];
    newCode[index] = value.slice(-1);
    setTwofaCode(newCode);

    if (value && index < 5) {
      codeInputRefs[index + 1].current?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !twofaCode[index] && index > 0) {
      codeInputRefs[index - 1].current?.focus();
    }
  };

  // Validate 2FA and complete login
  const validate2FA = async () => {
    if (!twofaCode.every(d => d !== '')) {
      setStepError({ step: 3, message: 'Please enter the 6-digit code' });
      return;
    }

    try {
      // Ensure we have a token before making the request
      const currentToken = tokenManager.getAccessToken();
      if (!currentToken) {
        setStepError({ step: 3, message: 'Session expired. Please log in again.' });
        return;
      }

      const code = twofaCode.join('');
      const result = await apiService.verify2FA(code, twofaMethod);
      
      // Save full access tokens (these are the full access tokens after 2FA verification)
      tokenManager.saveTokens(result.token, result.refreshToken, email);
      
      // Load user with the new tokens (don't call login() again - it would trigger another 2FA requirement)
      // Just load the user data and the AuthContext will handle the redirect
      await loadUser();
      
      // The AuthContext will handle redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid 2FA code';
      console.error('2FA verification error:', err);
      setStepError({ step: 3, message: errorMessage });
      setTwofaCode(['', '', '', '', '', '']);
      codeInputRefs[0].current?.focus();
    }
  };

  // Forgot password
  const handleForgotPassword = () => {
    goToStep('forgot');
  };

  const sendResetEmail = async () => {
    if (!forgotEmail || !forgotEmail.includes('@')) {
      setStepError({ step: 'forgot', message: 'Please enter a valid email' });
      return;
    }
    
    try {
      await apiService.requestPasswordReset(forgotEmail);
      goToStep('resetSent');
    } catch (err) {
      setStepError({ step: 'forgot', message: err instanceof Error ? err.message : 'Failed to send reset email' });
    }
  };

  const isPinComplete = pin.every(d => d !== '');
  const isCodeComplete = twofaCode.every(d => d !== '');

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <div className="login-logo-icon">VS</div>
            <div className="login-logo-text">VERSUS</div>
            <div className="login-logo-sub">Admin Portal</div>
          </div>

          {/* Step 1: Username & Password */}
          {currentStep === 1 && (
            <div className="login-step active">
              <div className="login-title">Welcome Back</div>
              <div className="login-subtitle">Enter your credentials to continue</div>
              
              {stepError?.step === 1 && (
                <div className="login-error show">
                  <span>⚠️</span>
                  <span>{stepError.message}</span>
                </div>
              )}

              <form onSubmit={handleCredentialsSubmit}>
                <div className="login-form-group">
                  <label className="login-label">Username or Email</label>
                  <input
                    type="text"
                    className="login-input"
                    placeholder="admin@versus.bet"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <div className="login-form-group">
                  <label className="login-label">Password</label>
                  <input
                    type="password"
                    className="login-input password-input"
                    placeholder="••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                <button type="submit" className="login-btn" disabled={isLoading}>
                  Continue →
                </button>
              </form>

              <div className="login-footer">
                <button type="button" onClick={handleForgotPassword} style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
                  Forgot password?
                </button>
              </div>
            </div>
          )}

          {/* Step: Forgot Password */}
          {currentStep === 'forgot' && (
            <div className="login-step active">
              <div className="login-title">🔑 Reset Password</div>
              <div className="login-subtitle">Enter your email to receive reset instructions</div>
              
              {stepError?.step === 'forgot' && (
                <div className="login-error show">
                  <span>⚠️</span>
                  <span>{stepError.message}</span>
                </div>
              )}

              <div className="login-form-group">
                <label className="login-label">Email Address</label>
                <input
                  type="email"
                  className="login-input"
                  placeholder="admin@versus.bet"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                />
              </div>

              <button className="login-btn" onClick={sendResetEmail}>
                Send Reset Link →
              </button>

              <div className="login-footer">
                <button type="button" onClick={() => goToStep(1)} style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
                  ← Back to login
                </button>
              </div>
            </div>
          )}

          {/* Step: Reset Sent */}
          {currentStep === 'resetSent' && (
            <div className="login-step active">
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '64px', marginBottom: '20px' }}>📧</div>
                <div className="login-title">Check Your Email</div>
                <div className="login-subtitle">
                  We've sent a password reset link to<br />
                  <strong>{forgotEmail || 'admin@versus.bet'}</strong>
                </div>
                <div style={{
                  background: 'var(--glass-highlight)',
                  borderRadius: 'var(--radius)',
                  padding: '16px',
                  margin: '24px 0',
                  fontSize: '13px',
                  color: 'var(--text-muted)'
                }}>
                  Didn't receive it? Check spam folder or{' '}
                  <button
                    type="button"
                    onClick={sendResetEmail}
                    style={{ color: 'var(--blue)', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                  >
                    resend
                  </button>
                </div>
                <button className="login-btn" onClick={() => goToStep(1)}>← Back to Login</button>
              </div>
            </div>
          )}

          {/* Step 2: PIN */}
          {currentStep === 2 && (
            <div className="login-step active">
              <div className="login-title">🔐 Enter PIN</div>
              <div className="login-subtitle">4-digit secret PIN + today's date (DD/MM)</div>
              
              {stepError?.step === 2 && (
                <div className="login-error show">
                  <span>⚠️</span>
                  <span>{stepError.message}</span>
                </div>
              )}

              <div className="pin-container">
                <div className="pin-group">
                  {[0, 1, 2, 3].map((index) => (
                    <input
                      key={index}
                      ref={pinInputRefs[index]}
                      type="password"
                      className="pin-input"
                      maxLength={1}
                      inputMode="numeric"
                      value={pin[index]}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      onKeyDown={(e) => handlePinKeyDown(index, e)}
                    />
                  ))}
                </div>
                <div className="pin-divider">+</div>
                <div className="pin-group">
                  <input
                    type="text"
                    className="pin-input date-suffix"
                    readOnly
                    value={dateSuffix.day[0] || ''}
                  />
                  <input
                    type="text"
                    className="pin-input date-suffix"
                    readOnly
                    value={dateSuffix.day[1] || ''}
                  />
                  <input
                    type="text"
                    className="pin-input date-suffix"
                    readOnly
                    value={dateSuffix.month[0] || ''}
                  />
                  <input
                    type="text"
                    className="pin-input date-suffix"
                    readOnly
                    value={dateSuffix.month[1] || ''}
                  />
                </div>
              </div>
              <div className="pin-hint">
                Your secret PIN <strong>+ today's date suffix</strong>
              </div>

              <button
                className="login-btn"
                onClick={validatePin}
                disabled={!isPinComplete}
              >
                Verify PIN →
              </button>

              <div className="login-footer">
                <button type="button" onClick={() => goToStep(1)} style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
                  ← Back to login
                </button>
              </div>
            </div>
          )}

          {/* Step 3: 2FA */}
          {currentStep === 3 && (
            <div className="login-step active">
              <div className="login-title">🛡️ Two-Factor Authentication</div>
              <div className="login-subtitle">Enter the code from your authenticator</div>
              
              {stepError?.step === 3 && (
                <div className="login-error show">
                  <span>⚠️</span>
                  <span>{stepError.message}</span>
                </div>
              )}

              <div className="twofa-options">
                <div
                  className={`twofa-option ${twofaMethod === 'app' ? 'selected' : ''}`}
                  onClick={() => setTwofaMethod('app')}
                >
                  <div className="twofa-option-icon">📱</div>
                  <div className="twofa-option-label">Auth App</div>
                </div>
                <div
                  className={`twofa-option ${twofaMethod === 'sms' ? 'selected' : ''}`}
                  onClick={() => setTwofaMethod('sms')}
                >
                  <div className="twofa-option-icon">💬</div>
                  <div className="twofa-option-label">SMS Code</div>
                </div>
              </div>

              <div className="twofa-code-inputs">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={codeInputRefs[index]}
                    type="text"
                    className="twofa-code-input"
                    maxLength={1}
                    inputMode="numeric"
                    value={twofaCode[index]}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  />
                ))}
              </div>

              <button
                className="login-btn"
                onClick={validate2FA}
                disabled={!isCodeComplete || isLoading}
                style={{ marginTop: '24px' }}
              >
                {isLoading ? 'Signing in...' : 'Sign In →'}
              </button>

              <div className="login-footer">
                <button type="button" onClick={() => goToStep(2)} style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
                  ← Back
                </button>
                <span style={{ margin: '0 12px', color: 'var(--glass-border)' }}>|</span>
                <button type="button" style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
                  Resend code
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;